import { useState } from "react";
import { useQueryClient } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import DeleteConfirmationModal from "../../components/app/deleteConfirmationModal.tsx";
import EnrolleeCertificateList from "../../components/app/enrolleeCertificateList.tsx";
import EnrolleeDetailsCard from "../../components/app/enrolleeDetailsCard.tsx";
import ErrorLabel from "../../components/form/errorLabel.tsx";
import LoadingSpinner from "../../components/loadingSpinner.tsx";
import PageHeader from "../../components/pageHeader.tsx";
import NotFoundPage from "../../global/notFoundPage.tsx";
import { useMutationDeleteEnrollee } from "../../hooks/mutations/useMutationDeleteEnrollee.ts";
import { QueryKeys } from "../../hooks/queries/queryKeys.ts";
import { useQueryGetCertificatesForEnrolleeId } from "../../hooks/queries/useQueryGetCertificatesForEnrolleeId.ts";
import { useQueryGetEnrolleeById } from "../../hooks/queries/useQueryGetEnrolleeById.ts";
import { useQueryGetProjectById } from "../../hooks/queries/useQueryGetProjectById.ts";
import { useQueryGetSourceById } from "../../hooks/queries/useQueryGetSourceById.ts";

export default function EnrolleeDetails() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { sourceId, projectId, enrolleeId } = useParams();

  const { data: enrollee, isFetching: isEnrolleeFetching } =
    useQueryGetEnrolleeById(sourceId, projectId, enrolleeId);
  const { data: certificates, isFetching: isCertificatesFetching } =
    useQueryGetCertificatesForEnrolleeId(sourceId, projectId, enrolleeId);
  const { data: project, isFetching: isFetchingProject } =
    useQueryGetProjectById(sourceId, projectId);

  const { data: source, isFetching: isFetchingSource } = useQueryGetSourceById(
    project?.source.id,
  );

  const {
    mutate: deleteEnrollee,
    isLoading: isEnrolleeDeleting,
    error: enrolleeDeleteError,
  } = useMutationDeleteEnrollee();

  const [isEnrolleeDeleteModalOpen, setIsEnrolleeDeleteModalOpen] =
    useState(false);

  if (enrolleeId == null) {
    return <NotFoundPage />;
  }

  if (
    isEnrolleeFetching ||
    isCertificatesFetching ||
    isFetchingProject ||
    isFetchingSource
  ) {
    return <LoadingSpinner />;
  }

  if (source == null || project == null || enrollee == null) {
    return <NotFoundPage />;
  }

  return (
    <div className="h-full flex flex-col">
      <div className="p-12 h-full overflow-y-auto">
        <PageHeader title={"Enrollee Details"} />
        {enrolleeDeleteError != null && (
          <ErrorLabel message={enrolleeDeleteError?.toString()} />
        )}
        <div className="mt-12 flex flex-col gap-12">
          <EnrolleeDetailsCard
            source={source}
            project={project}
            enrollee={enrollee}
            onEdit={() =>
              navigate(
                `/sources/${sourceId}/projects/${projectId}/enrollees/${enrolleeId}`,
              )
            }
            onDelete={() => {
              setIsEnrolleeDeleteModalOpen(true);
            }}
          />
          <EnrolleeCertificateList
            source={source}
            project={project}
            enrollee={enrollee}
            certificates={certificates ?? []}
            onAddCertificate={() =>
              navigate(
                `/sources/${sourceId}/projects/${projectId}/enrollees/${enrolleeId}/certificates/new`,
              )
            }
          />
        </div>
      </div>
      <DeleteConfirmationModal
        entityName="enrollee"
        isOpen={isEnrolleeDeleteModalOpen}
        isLoading={isEnrolleeDeleting}
        onConfirm={() => {
          if (enrollee == null) {
            setIsEnrolleeDeleteModalOpen(false);
            return;
          }

          deleteEnrollee(
            {
              sourceId: source.id,
              projectId: project.id,
              enrolleeId: enrollee.id,
            },
            {
              onSuccess: () => {
                queryClient.invalidateQueries([
                  QueryKeys.GetProjectEnrollees,
                  project.id,
                ]);
                setIsEnrolleeDeleteModalOpen(false);
                navigate(`/sources/${sourceId}/projects/${projectId}/details`);
              },
              onError: (error) =>
                console.error("Error deleting enrollee", error),
            },
          );
        }}
        onClose={() => setIsEnrolleeDeleteModalOpen(false)}
      />
    </div>
  );
}
